/**
 * Dev Seed Manager — export/import helpers for the hosted dev app's showcase data.
 * Pure functions (no Firestore) so they can be unit-tested independently.
 */

import { parseTestAssignmentsManifest, PUBLIC_MANIFEST_URL } from '@/utils/testAssignments'

export const SHOWCASE_COLLECTIONS = [
  { id: 'users', name: 'Users', description: 'Staff accounts, roles, Aeries IDs', importOrder: 1 },
  {
    id: 'schools',
    name: 'Schools',
    description: 'District school registry (elementary, middle, high)',
    importOrder: 2
  },
  {
    id: 'students',
    name: 'Students',
    description: 'IEP/504 records, schedules, services',
    importOrder: 3
  },
  {
    id: 'app_settings',
    name: 'App Settings',
    description: 'Periods, class services, time tables, school calendar',
    importOrder: 4
  },
  {
    id: 'refDocuments',
    name: 'Reference Documents',
    description: 'Shared reference documents',
    importOrder: 5
  },
  {
    id: 'meetings',
    name: 'Meetings',
    description: 'Scheduled IEP/504 meetings & calendar events',
    importOrder: 6
  },
  {
    id: 'feedbackTemplates',
    name: 'Feedback Templates',
    description: 'Demo IEP teacher-input templates for video walkthroughs',
    importOrder: 7
  },
  {
    id: 'feedbackInstances',
    name: 'Feedback Instances',
    description: 'Sent feedback forms with relative due dates',
    importOrder: 8
  },
  {
    id: 'feedbackResponses',
    name: 'Feedback Responses',
    description: 'Per-teacher response docs (pending / submitted mix)',
    importOrder: 9
  },
  {
    id: 'genEdHours',
    name: 'Gen-Ed Hours',
    description: 'General-education hours tracking',
    importOrder: 10
  },
  {
    id: 'extendedAccessStudents',
    name: 'Extended Access Students',
    description: 'Per-student extended-access link records',
    importOrder: 11
  },
  {
    id: 'aideSchedules',
    name: 'Aide Schedules',
    description: 'Para direct and class assignments',
    importOrder: 12
  },
  {
    id: 'extendedAccess',
    name: 'Extended Access',
    description: 'Per-student extended access grants',
    importOrder: 13
  },
  { id: 'config', name: 'Configuration', description: 'Misc config documents', importOrder: 14 }
]

// Collections whose documents contain user/student ID references that must be
// remapped on import (deepRemapIds). users is excluded (handled separately).
export const REMAPPED_COLLECTIONS = [
  'students',
  'app_settings',
  'aideSchedules',
  'extendedAccess',
  'config',
  'refDocuments',
  'meetings',
  'genEdHours',
  'extendedAccessStudents',
  'feedbackTemplates',
  'feedbackInstances',
  'feedbackResponses'
]

/** Fetch test assignment manifest from hosted public path (optional). */
export async function fetchTestAssignmentsManifest() {
  try {
    const res = await fetch(PUBLIC_MANIFEST_URL)
    if (!res.ok) return null
    const raw = await res.json()
    return parseTestAssignmentsManifest(raw)
  } catch {
    return null
  }
}

export const DEMO_IMPORT_FILES = [
  {
    id: 'ava-hill-feedback',
    name: 'ava-hill_feedback_pack.json',
    label: 'Ava Hill — completed Teacher Feedback',
    description:
      'Small additive pack (1 template, 1 instance, 6 responses). Use the one-click import button above, or download and import manually.',
    path: '/seed-demo/ava-hill_feedback_pack.json',
    adminTab: 'dev-seed',
    isJson: true
  },
  {
    id: 'showcase-pack',
    name: 'dev_showcase_pack.json',
    label: 'Full Showcase Pack (300 students)',
    description:
      'Pre-built Firestore snapshot — import via section below (Overwrite + Remap users recommended)',
    path: '/seed-demo/dev_showcase_pack.json',
    adminTab: 'dev-seed',
    isJson: true
  },
  {
    id: 'test-assignments',
    name: 'test_assignments.json.example',
    label: 'Test Assignment Manifest',
    description:
      'Real-email → role/school wiring for seed import. Copy to test_assignments.json and deploy to public/seed-demo/',
    path: '/seed-demo/test_assignments.example.json',
    adminTab: 'dev-seed',
    isJson: true
  },
  {
    id: 'bulk-users',
    name: 'bulk_users.csv',
    label: 'Bulk User Import',
    description: 'Sample staff CSV for Admin → Add Users bulk import (~87 roles)',
    path: '/seed-demo/bulk_users.csv',
    adminTab: 'usersAdd'
  },
  {
    id: 'bulk-students',
    name: 'generated_students_seed.csv',
    label: 'Bulk Student Import',
    description: 'Sample student CSV for Admin → Add Students (import users first)',
    path: '/seed-demo/generated_students_seed.csv',
    adminTab: 'addStudents'
  },
  {
    id: 'aeries-schedule',
    name: 'aeries-schedule-mock.csv',
    label: 'Aeries Schedule Import',
    description: 'Long-format Aeries schedule export for Schedule Import demo',
    path: '/seed-demo/aeries-schedule-mock.csv',
    adminTab: 'student-import'
  },
  {
    id: 'seis',
    name: 'seis-demo.csv',
    label: 'SEIS Import',
    description: 'Sample SEIS export — matches students already in the database by SSID',
    path: '/seed-demo/seis-demo.csv',
    adminTab: 'student-import'
  },
  {
    id: 'school-calendar',
    name: 'school-calendar-demo.csv',
    label: 'School Calendar Import',
    description: 'Aeries-style instructional calendar for Admin → School Calendar',
    path: '/seed-demo/school-calendar-demo.csv',
    adminTab: 'school-calendar'
  },
  {
    id: 'time-table-7',
    name: 'cvc_7th_grade_mon_thu.csv',
    label: 'Time Table (7th Mon–Thu)',
    description: 'Bell schedule CSV for Admin → Time Table import',
    path: '/seed-demo/cvc_7th_grade_mon_thu.csv',
    adminTab: 'time-table'
  },
  {
    id: 'aide-schedule-grid',
    name: 'aide-schedule-para-grid.csv',
    label: 'Aide Schedule Import (Para Grid)',
    description:
      'Sample para schedule spreadsheet for Aide Master Schedule → Import. Names match bulk_users.csv / showcase staff.',
    path: '/seed-demo/aide-schedule-para-grid.csv',
    adminTab: 'aide-schedule-master',
    openPath: '/aide-schedule-master'
  },
  // High school setup wizard pack (distinct SSIDs / @demohs.setup.edu staff)
  {
    id: 'hs-setup-users',
    name: 'hs-setup-users.csv',
    label: 'HS Setup — Users',
    description: 'High school staff (@demohs.setup.edu) for School setup wizard testing',
    path: '/seed-demo/hs-setup/hs-setup-users.csv',
    adminTab: 'staffImport'
  },
  {
    id: 'hs-setup-timetable',
    name: 'hs-setup-timetable-mon-thu.csv',
    label: 'HS Setup — Time Table (Mon–Thu)',
    description: 'High school bell schedule for Admin → Time Table',
    path: '/seed-demo/hs-setup/hs-setup-timetable-mon-thu.csv',
    adminTab: 'time-table'
  },
  {
    id: 'hs-setup-timetable-friday',
    name: 'hs-setup-timetable-friday.csv',
    label: 'HS Setup — Time Table (Friday)',
    description: 'High school Friday minimum schedule',
    path: '/seed-demo/hs-setup/hs-setup-timetable-friday.csv',
    adminTab: 'time-table'
  },
  {
    id: 'hs-setup-calendar',
    name: 'hs-setup-calendar.csv',
    label: 'HS Setup — School Calendar',
    description: '2026–2027 instructional calendar for Demo High School',
    path: '/seed-demo/hs-setup/hs-setup-calendar.csv',
    adminTab: 'school-calendar'
  },
  {
    id: 'hs-setup-aeries-students',
    name: 'hs-setup-aeries-students.csv',
    label: 'HS Setup — Aeries Export (long format)',
    description:
      'Real Aeries shape: one row per student-period with teacher Email. Import via Aeries Schedule Import (users first).',
    path: '/seed-demo/hs-setup/hs-setup-aeries-students.csv',
    adminTab: 'student-import'
  },
  {
    id: 'hs-setup-seis',
    name: 'hs-setup-seis.csv',
    label: 'HS Setup — SEIS',
    description: 'SEIS export matching HS Setup Aeries SSIDs',
    path: '/seed-demo/hs-setup/hs-setup-seis.csv',
    adminTab: 'student-import'
  },
  {
    id: 'hs-setup-teacher-schedule',
    name: 'hs-setup-teacher-schedule.html',
    label: 'HS Setup — Teacher Schedule (Document Bank)',
    description:
      'Master schedule for all HS Setup teachers (one PREP each). Upload in Reference Documents as type Teacher Schedule.',
    path: '/seed-demo/hs-setup/hs-setup-teacher-schedule.html',
    adminTab: 'ref-documents'
  },
  {
    id: 'ms2-users',
    name: 'ms2-users.csv',
    label: 'MS2 — Users',
    description:
      'Demo Middle School 2 staff (@demoms2.setup.edu). Import first so schedule teacher emails resolve.',
    path: '/seed-demo/ms2-import/ms2-users.csv',
    adminTab: 'staffImport'
  },
  {
    id: 'ms2-aeries-schedule',
    name: 'ms2-aeries-schedule.csv',
    label: 'MS2 — Aeries Schedule',
    description:
      'Long-format Aeries export. Classroom teachers plus non-teacher staff (admin, SLP CM, providers) so the importer can offer Add to Teacher List. SSIDs 1000050001+.',
    path: '/seed-demo/ms2-import/ms2-aeries-schedule.csv',
    adminTab: 'student-import'
  },
  {
    id: 'ms2-seis',
    name: 'ms2-seis.csv',
    label: 'MS2 — SEIS',
    description:
      'SEIS with Student Eligibility Status / Student Exited, initials, and CM emails matching MS2 users.',
    path: '/seed-demo/ms2-import/ms2-seis.csv',
    adminTab: 'student-import'
  },
  {
    id: 'ms2-aeries-contacts',
    name: 'ms2-aeries-contacts.csv',
    label: 'MS2 — Aeries Contacts',
    description:
      'Long-format CON export for MS2 SSIDs (Quincy Marks has no row — blank never clears).',
    path: '/seed-demo/ms2-import/ms2-aeries-contacts.csv',
    adminTab: 'student-import'
  },
  {
    id: 'ms2-aeries-504',
    name: 'ms2-aeries-504.csv',
    label: 'MS2 — Aeries 504',
    description:
      'FOF/FAP export covering pendingIep, superseded, exited, conflict, stale, overdue, and empty-shell cases.',
    path: '/seed-demo/ms2-import/ms2-aeries-504.csv',
    adminTab: 'student-import'
  },
  {
    id: 'middle-aeries-504',
    name: 'middle-aeries-504.csv',
    label: 'Middle — Aeries 504',
    description:
      '504 accommodations for Demo Middle School showcase students (SSIDs 1000020094+). Switch to that school, then upload on Student Import.',
    path: '/seed-demo/middle-import/middle-aeries-504.csv',
    adminTab: 'student-import'
  }
]

export const IMPORT_ORDER = [...SHOWCASE_COLLECTIONS].sort((a, b) => a.importOrder - b.importOrder)

/**
 * Walk a value tree and replace string IDs using idMap.
 * Preserves Firestore Timestamp-like objects.
 */
export function deepRemapIds(value, idMap) {
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return idMap[value] ?? value
  if (Array.isArray(value)) return value.map((item) => deepRemapIds(item, idMap))
  if (typeof value !== 'object') return value

  if (typeof value.seconds === 'number' && typeof value.nanoseconds === 'number') {
    return value
  }

  const out = {}
  for (const [key, nested] of Object.entries(value)) {
    out[key] = deepRemapIds(nested, idMap)
  }
  return out
}

/**
 * Build packUserId → firebaseUid map by matching email (case-insensitive) or aeriesId.
 */
export function buildUserIdMap(
  packUsers = [],
  existingUsers = [],
  { matchByEmail = true, matchByAeriesId = true } = {}
) {
  const idMap = {}

  const existingByEmail = new Map()
  const existingByAeriesId = new Map()
  for (const user of existingUsers) {
    if (user.email) existingByEmail.set(user.email.toLowerCase(), user)
    if (user.aeriesId != null && user.aeriesId !== '') {
      existingByAeriesId.set(String(user.aeriesId), user)
    }
  }

  for (const item of packUsers) {
    const packId = item.id
    if (!packId) continue

    let match = null
    if (matchByEmail && item.email) {
      match = existingByEmail.get(String(item.email).toLowerCase())
    }
    if (!match && matchByAeriesId && item.aeriesId != null && item.aeriesId !== '') {
      match = existingByAeriesId.get(String(item.aeriesId))
    }

    idMap[packId] = match?.id ?? packId
  }

  return idMap
}

/** Add aeriesId → firebaseUid entries so legacy seed data remaps correctly. */
export function extendIdMapWithAeriesIds(idMap, users = []) {
  const extended = { ...idMap }
  for (const user of users) {
    if (user.aeriesId != null && user.aeriesId !== '' && user.id) {
      extended[String(user.aeriesId)] = user.id
    }
  }
  return extended
}

export function summarizePack(pack) {
  const collections = pack?.collections || {}
  return SHOWCASE_COLLECTIONS.map((col) => ({
    ...col,
    count: Array.isArray(collections[col.id]) ? collections[col.id].length : 0
  }))
}

export function validatePack(pack) {
  if (!pack || typeof pack !== 'object') {
    return { valid: false, error: 'File is not a valid JSON object.' }
  }
  if (!pack.collections || typeof pack.collections !== 'object') {
    return {
      valid: false,
      error: 'Missing collections object. Use Export from this page or Backup & Restore format.'
    }
  }
  return { valid: true }
}

export function createExportMetadata(extra = {}) {
  return {
    version: '1.0',
    type: 'casemanagevue-dev-showcase',
    createdAt: new Date().toISOString(),
    ...extra
  }
}

export function downloadJsonFile(data, filename) {
  const json = JSON.stringify(data, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
